<template>
  <div class="container relative flex flex-col w-full h-full overflow-hidden">
    <!-- HEADER -->
    <tx-form-header class="flex flex-col mx-2 my-[30px] grow-0 shrink-0 justify-center header" :title="t('shareTags.title')" :show-header="true" />
    <!-- BODY -->
    <loader v-if="loading" class="h-full" />
    <div v-else class="h-full m-2 w-full h-full overflow-auto body">
      <div class="inline-flex flex-wrap mt-10">
        <div v-for="tag in tags" :key="tag.Tag">
          <div
            class="flex items-center p-2.5 pr-3 m-1 rounded-md check-box-tag h-10 cursor-pointer"
            :style="{ backgroundColor: tag.Color + (selectedTagIds[tag.Id] ? 'FF' : '33'), border: `1px solid ${tag.Color}4C`, color: selectedTagIds[tag.Id] ? 'white' : 'inherit' }"
          >
            <tx-checkbox v-model="selectedTagIds[tag.Id]" class="cursor-pointer" :label="tag.Tag" @change="tagSelectionChange(tag)" />
            <font-awesome-icon v-if="((tag.SharedUsers?.length) || (tag.SharedUsersGroups?.length))" class="w-3 h-3 ml-1" icon="fa-light fa-share" />
          </div>
        </div>
      </div>
      <div class="mt-10 h-[calc(100%_-_100px)] overflow-hidden">
        <div class="mb-2 text-md">
          {{ t('shareTags.selectTags') }}
        </div>
        <div class="flex h-[300px]">
          <!-- Available -->
          <div class="w-[49%] mr-1 border rounded-md h-full">
            <div class="h-10 text-sm text-center bg-gray-200">
              <tx-input v-model="filter" :placeholder="t('shareUserDialog.filter')" />
            </div>
            <ul tabindex="-1" role="listbox" class="w-full overflow-y-auto leading-10 bg-white h-[250px]">
              <li
                v-for="(accessibleItem) in availableAccessibleItems" :key="`${accessibleItem.type}${accessibleItem.id}`" tabindex="0"
                class="relative flex items-center h-10 cursor-pointer hover:bg-active"
                @click="onAccessibleItemSelected(accessibleItem)"
              >
                <div class="flex flex-1 m-1 text-sm border-b border-grey">
                  <div class="w-full text-left text-primary-500 line-clamp-1">
                    {{ accessibleItem.name }}
                  </div>
                  <div class="w-full text-xs text-right line-clamp-1">
                    {{ accessibleItem.subTitle }}
                  </div>
                </div>
              </li>
            </ul>
            <div class="h-10 p-1 pr-4 bg-gray-200">
              <tx-button type="text" :text="t('shareUserDialog.addAll')" height="32px" @click="onAddAll" />
            </div>
          </div>

          <!-- Selected -->
          <div class="w-[49%] ml-1 border rounded-md h-full">
            <div class="flex items-center h-10 text-sm bg-gray-200">
              <p class="w-full text-center">
                {{ t('shareUserDialog.nSelectedUsers', modelValue.length) }}
              </p>
            </div>
            <ul tabindex="-1" role="listbox" class="w-full overflow-y-auto leading-10 h-[250px]">
              <li
                v-for="accessibleItem in modelValue" :key="accessibleItem.id" tabindex="0"
                class="relative flex items-center h-10 cursor-pointer hover:bg-active"
                @click="onSelectedAccessibleItemSelected(accessibleItem)"
              >
                <div class="flex flex-1 m-1 text-sm border-b border-grey">
                  <div class="w-full text-left text-primary-500 line-clamp-1">
                    {{ accessibleItem.name }}
                  </div>
                  <div class="w-full text-xs text-right line-clamp-1">
                    {{ accessibleItem.subTitle }}
                  </div>
                </div>
              </li>
            </ul>
            <div class="h-10 p-1 pr-4 bg-gray-200">
              <tx-button type="text" :text="t('shareUserDialog.removeAll')" height="32px" @click="onRemoveAll" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- FOOTER -->
    <tx-form-footer
      v-if="!loading"
      class="flex flex-row justify-end flex-shrink-0 flex-nowrap"
      :primary-text="t('general.update')" :primary-disabled="Object.values(selectedTagIds).filter(value => value === true).length === 0"
      @primary-click="share" @secondary-click="onCancel"
    />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, onMounted, reactive, ref } from 'vue'
import { clone } from 'lodash-es'
import Loader from '@/shared/components/Loader.vue'
import TxInput from '@/shared/components/TxInput.vue'
import TxButton from '@/shared/components/TxButton.vue'
import type { AccessibleUserGroupModel, AccessibleUserModel } from '@/api/t1/model/userModel'
import { getAccessibleUsers, getAccessibleUsersGroups } from '@/api/t1/user'
import utils from '@/services/utils'
import useErrorMessage from '@/shared/composables/errorMessage'
import { useUserStore } from '@/store/userData'
import { useNotificationStore } from '@/store/notification'
import TxFormHeader from '@/shared/components/forms/TxFormHeader.vue'
import appConfig from '@/services/appConfig'
import type FavoriteTag from '@/models/favoriteTag'
import TxCheckbox from '@/shared/components/TxCheckbox.vue'
import TxFormFooter from '@/shared/components/forms/TxFormFooter.vue'
import { shareFavorites } from '@/api/t1/favorite'

export interface IAccessibleItem {
  id: number
  type: AccessibleItemType
  name: string
  subTitle: string
}

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'tagShared'): void
}>()

const { t } = useI18n()
const userStore = useUserStore()
const { errorMessage } = useErrorMessage()
const notificationStore = useNotificationStore()
let tags: FavoriteTag[] = []
const indexedTags: Record<number, FavoriteTag> = {}
const selectedTagIds = reactive<Record<number, boolean>>({})
const loading = ref(false)
const filter = ref('')
const accessibleItems = ref<IAccessibleItem[]>([])
const indexedAccessibleUsersGroups = ref<Record<number, AccessibleUserGroupModel>>({})
const indexedAccessibleUsers = ref<Record<number, AccessibleUserModel>>({})
const preSelectedUsers = ref<IAccessibleItem[]>([])
const inactiveUsersGroupsList = ref<number[]>([])
const inactiveUsersList = ref<number[]>([])
const modelValue = ref<IAccessibleItem[]>([])
const selectedUserIds = ref<number[]>([])

const availableAccessibleItems = computed(() => {
  const selectedItemIds = new Set(modelValue.value.map(x => x.id))
  if (utils.isValidStringValue(filter.value)) {
    return accessibleItems.value.filter(x => !selectedItemIds.has(x.id) && (x.name.toLowerCase().includes(filter.value.toLowerCase()) || x.subTitle.toLowerCase().includes(filter.value.toLowerCase())))
  }
  return accessibleItems.value.filter(x => !selectedItemIds.has(x.id))
})

async function init() {
  accessibleItems.value = []
  if (userStore.activeCatalog) {
    loading.value = true
    const catalogCode = userStore.activeCatalog.CatalogCode
    const customerId = userStore.currentCustomer ? userStore.currentCustomer.CustomerId : undefined
    tags = await appConfig.DB!.favorites.where({ CatalogCode: userStore.activeCatalog!.CatalogCode, UserId: userStore.userProfile.Id, CreatedByUserName: userStore.currentUsername, Status: 1 }).toArray()
    tags.forEach((tag) => {
      indexedTags[tag.Id] = tag
      selectedTagIds[tag.Id] = false
    })
    const promises = [getAccessibleUsers(catalogCode, customerId), getAccessibleUsersGroups()]

    Promise.all(promises)
      .then(async (responses) => {
        for (const userGroup of responses[1].data as AccessibleUserGroupModel[]) {
          if (userGroup.Status) {
            accessibleItems.value.push({
              id: userGroup.Id,
              type: 'group',
              name: userGroup.Name,
              subTitle: userGroup.AccountName,
            })
          }
        }
        for (const user of responses[0].data as AccessibleUserModel[]) {
          if (user.IsAccessible && user.Status) {
            accessibleItems.value.push({
              id: user.Id,
              type: 'user',
              name: `${user.FirstName} ${user.LastName}`,
              subTitle: user.UserName,
            })
          }
        }
      })
      .catch((e) => {
        console.error(e)
        errorMessage.value = t('general.unexpectedError')
      })
      .finally(() => {
        loading.value = false
      })
  }
}

function tagSelectionChange(tag) {
  // we are adding
  if (selectedTagIds[tag.Id]) {
    if (tag.SharedUsers && tag.SharedUsers.length) {
      tag.SharedUsers.forEach((userId) => {
        const isPartOfModelValue = modelValue.value.find(user => user.id === userId && user.type === 'user')
        if (!isPartOfModelValue) {
          const accessibleItem = availableAccessibleItems.value.find(user => user.id === userId && user.type === 'user')
          if (accessibleItem) {
            selectedUserIds.value.push(userId)
            modelValue.value.push(accessibleItem)
          }
          else {
            inactiveUsersList.value.push(userId)
          }
        }
      })
    }
    if (tag.SharedUsersGroups && tag.SharedUsersGroups.length) {
      tag.SharedUsersGroups.forEach((groupId) => {
        const isPartOfModelValue = modelValue.value.find(group => group.id === groupId && group.type === 'group')
        if (!isPartOfModelValue) {
          const accessibleItem = availableAccessibleItems.value.find(group => group.id === groupId && group.type === 'group')
          if (accessibleItem) {
            selectedUserIds.value.push(groupId)
            modelValue.value.push(accessibleItem)
          }
          else {
            inactiveUsersGroupsList.value.push(groupId)
          }
        }
      })
    }
  }
  else {
    // we are removing
    // only unselect users that are not part of other selected tags
    const checkedTags: number[] = []
    Object.keys(selectedTagIds).forEach((id) => {
      if (selectedTagIds[id]) {
        checkedTags.push(Number(id))
      }
    })
    if (tag.SharedUsers && tag.SharedUsers.length) {
      const usersToBeRemoved = clone(tag.SharedUsers)

      for (let i = usersToBeRemoved.length - 1; i >= 0; i--) {
        const userIdToRemove = usersToBeRemoved[i]
        let found = false
        // check rest of tags and if any of them have any of these user, remove the user from the to be removed list (do not un-select that user)
        for (let i = 0; i < checkedTags.length; i++) {
          const checkedTag = indexedTags[checkedTags[i]]
          if (checkedTag.Id !== tag.id && checkedTag.SharedUsers && checkedTag.SharedUsers.length && checkedTag.SharedUsers.includes(userIdToRemove)) {
            found = true
            break
          }
        }
        if (found) {
          // remove the user id from the list user ids that is going to be removed
          usersToBeRemoved.splice(i, 1)
          continue
        }
      }

      // remove usersToBeRemoved from  selectedUsers and remove userId from selectedUserIds
      if (usersToBeRemoved.length) {
        usersToBeRemoved.forEach((id) => {
          const index = modelValue.value.findIndex(user => user.type === 'user' && user.id === id)
          if (index !== -1) {
            modelValue.value.splice(index, 1)
          }
        })
      }
    }
    if (tag.SharedUsersGroups && tag.SharedUsersGroups.length) {
      const groupsToBeRemoved = clone(tag.SharedUsersGroups)

      for (let i = groupsToBeRemoved.length - 1; i >= 0; i--) {
        const userIdToRemove = groupsToBeRemoved[i]
        let found = false
        // check rest of tags and if any of them have any of these user, remove the user from the to be removed list (do not un-select that user)
        for (let i = 0; i < checkedTags.length; i++) {
          const checkedTag = indexedTags[checkedTags[i]]
          if (checkedTag.Id !== tag.id && checkedTag.SharedUsersGroups && checkedTag.SharedUsersGroups.length && checkedTag.SharedUsersGroups.includes(userIdToRemove)) {
            found = true
            break
          }
        }
        if (found) {
          // remove the user id from the list user ids that is going to be removed
          groupsToBeRemoved.splice(i, 1)
          continue
        }
      }

      // remove usersToBeRemoved from  selectedUsers and remove userId from selectedUserIds
      if (groupsToBeRemoved.length) {
        groupsToBeRemoved.forEach((id) => {
          const index = modelValue.value.findIndex(user => user.type === 'group' && user.id === id)
          if (index !== -1) {
            modelValue.value.splice(index, 1)
          }
        })
      }
    }
  }
}
function onAccessibleItemSelected(accessibleItem: IAccessibleItem) {
  selectedUserIds.value.push(accessibleItem.id)
  modelValue.value.push(accessibleItem)
}

function onSelectedAccessibleItemSelected(accessibleItem: IAccessibleItem) {
  const preSelectIndex = modelValue.value.findIndex(user => user.id === accessibleItem.id)
  if (preSelectIndex !== -1) {
    selectedUserIds.value.splice(preSelectIndex, 1)
    modelValue.value.splice(preSelectIndex, 1)
  }
}

function onAddAll() {
  if (availableAccessibleItems.value.length) {
    availableAccessibleItems.value.forEach((value) => {
      // if (!selectedUserIds.value.includes(value.id)) {
      selectedUserIds.value.push(value.id)
      modelValue.value.push(value)
      // }
    })
  }
}

function onRemoveAll() {
  modelValue.value = []
}
function onCancel() {
  resetData()
  emit('cancel')
}
function share() {
  if (userStore.activeCatalog) {
    loading.value = true
    let selectedUsersIdList: number[] = []
    let selectedUsersGroupsIdList: number[] = []
    // Filter and create 2 list for user groups and users
    modelValue.value.forEach((user) => {
      if (user.type === 'group') {
        if (!selectedUsersGroupsIdList.includes(user.id)) {
          selectedUsersGroupsIdList.push(user.id)
        }
      }
      else {
        if (!selectedUsersIdList.includes(user.id)) {
          selectedUsersIdList.push(user.id)
        }
      }
    })
    // If tag is shared with any user then appending the inactive users list with which these tags are shared before
    selectedUsersIdList = selectedUsersIdList.concat(inactiveUsersList.value)
    selectedUsersGroupsIdList = selectedUsersGroupsIdList.concat(inactiveUsersGroupsList.value)
    const checkedTags: number[] = []
    Object.keys(selectedTagIds).forEach((id) => {
      if (selectedTagIds[id]) {
        checkedTags.push(Number(id))
      }
    })
    shareFavorites(userStore.activeCatalog.CatalogCode, checkedTags, selectedUsersIdList, selectedUsersGroupsIdList)
      .then(async () => {
        notificationStore.addNotification({ message: t('shareTags.sharedTagsWithUserSuccessfully'), type: 'Success' })
        await userStore.doLoadData(['MyFavoriteTags'])
        emit('tagShared')
      })
      .catch(() => {
        notificationStore.addNotification({ message: t('shareTags.sharedTagsWithUserFailed'), type: 'Alert' })
      })
      .then(() => {
        loading.value = false
        onCancel()
      })
  }
}
function resetData() {
  indexedAccessibleUsersGroups.value = {}
  indexedAccessibleUsers.value = {}
  preSelectedUsers.value = []
  inactiveUsersGroupsList.value = []
  inactiveUsersList.value = []
  modelValue.value = []
  errorMessage.value = ''
  filter.value = ''
}
onMounted(async () => {
  await init()
})
</script>
